import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getConviventies } from '../services/conviventieService';
import { getGemeenschappen, getGemeenschappenForUser } from '../services/gemeenschappen';
import { canManageConviventieTarget, normalizeUitnodigingen } from '../services/conviventieAccess';
import { formatGemeenschapFullLabel, formatGemeenschapLabel } from '../services/conviventieLabels';
import { deriveLidAantalFromStatus } from '../config/ledenUploadTemplate';
import {
  addUploadedLid,
  deleteUploadedLid,
  getUploadedLeden,
  updateUploadedLid
} from '../services/ledenUploadService';
import LedenList from '../components/LedenList';
import LedenUploadPanel from '../components/LedenUploadPanel';
import EditLidModal from '../components/EditLidModal';
import SectionHelpButton from '../components/SectionHelpButton';
import EmptyState from '../components/EmptyState';
import { trackMemberAdded, trackMemberEdited, trackMemberDeleted } from '../services/analytics';
import './LedenPage.css';

const emptyLidForm = {
  voornaam: '',
  achternaam: '',
  aantal: '',
  status: '',
  bijzonderheden: '',
  geboortedatum: '',
  email: '',
  telefoon: ''
};

const CONFIRMED_HINT_MESSAGE =
  'Deze ledenlijst is bevestigd. Bevestigde leden blijven bevestigd, inclusief aankomst, vertrek en bijzonderheden. Alleen een nieuwe Excel-upload wist de bevestiging.';

/**
 * Leden Page
 *
 * Manages the (ephemeral, conviventie-scoped) ledenlijst of one invited
 * gemeenschap or gastengroep per conviventie: the Excel import lives here,
 * together with add/edit/remove of individual members.
 */
const LedenPage = () => {
  const { user, isAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const userEmail = user?.email || '';

  // The Conviventies page hands a target over through the URL (see its
  // LedenUploadHandoffPanel) so it can be preselected here.
  const requestedConviventieId = searchParams.get('conviventie') || '';
  const requestedTargetType = searchParams.get('targetType') || '';
  const requestedTargetId = searchParams.get('targetId') || '';
  const requestedTargetKey = requestedTargetType && requestedTargetId
    ? `${requestedTargetType}:${requestedTargetId}`
    : '';

  const [conviventies, setConviventies] = useState([]);
  const [gemeenschappen, setGemeenschappen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ledenLoading, setLedenLoading] = useState(false);
  const [ledenRefreshToken, setLedenRefreshToken] = useState(0);

  const [selectedConviventieId, setSelectedConviventieId] = useState(requestedConviventieId);
  const [selectedTargetKey, setSelectedTargetKey] = useState(requestedTargetKey);

  const [leden, setLeden] = useState([]);
  const [uploadMeta, setUploadMeta] = useState({ uploadedAt: null, uploadedBy: '' });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLid, setEditingLid] = useState(null);
  const [formData, setFormData] = useState(emptyLidForm);
  const [formErrors, setFormErrors] = useState({});

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [conviventieRows, gemeenschapRows] = await Promise.all([
        getConviventies(),
        isAdmin ? getGemeenschappen() : getGemeenschappenForUser(userEmail)
      ]);

      setConviventies(conviventieRows);
      setGemeenschappen(gemeenschapRows);
    } catch (error) {
      console.error('Error loading leden data:', error);
      alert('Fout bij het laden van de ledenlijsten');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, userEmail]);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user, loadData]);

  const gemeenschapById = useMemo(() => {
    const map = new Map();
    gemeenschappen.forEach((item) => map.set(item.id, item));
    return map;
  }, [gemeenschappen]);

  const targetOptionsFor = useCallback((conviventie) => {
    if (!conviventie) return [];

    const options = [];

    normalizeUitnodigingen(conviventie.uitgenodigdeGemeenschappen).forEach((gemeenschap) => {
      const gemeenschapDoc = gemeenschapById.get(gemeenschap?.gemeenschapId);
      const canManage = canManageConviventieTarget(
        gemeenschap,
        gemeenschapDoc,
        { isAdmin, userEmail }
      );

      if (!canManage) return;

      options.push({
        key: `gemeenschap:${gemeenschap.gemeenschapId}`,
        targetType: 'gemeenschap',
        targetId: gemeenschap.gemeenschapId,
        // Show the same "Parochie - Gemeenschap" qualifier as the Uitgenodigde
        // list, falling back to the current gemeenschap document when the
        // invitation snapshot predates that field.
        label: formatGemeenschapLabel({
          gemeenschapNaam: gemeenschap.gemeenschapNaam,
          parochie: gemeenschap.parochie || gemeenschapDoc?.parochie
        }),
        // Full "Bisdom - Parochie - Gemeenschap" definition used for the download
        // file name, falling back to the current document for legacy snapshots.
        downloadName: formatGemeenschapFullLabel({
          bisdom: gemeenschap.bisdom || gemeenschapDoc?.bisdom,
          parochie: gemeenschap.parochie || gemeenschapDoc?.parochie,
          gemeenschapNaam: gemeenschap.gemeenschapNaam
        }),
        uploadStatus: gemeenschap.uploadStatus || 'pending',
        uploadedAt: gemeenschap.uploadedAt || null,
        uploadedBy: gemeenschap.uploadedBy || '',
        bevestigdeLeden: Array.isArray(gemeenschap.bevestigdeLeden) ? gemeenschap.bevestigdeLeden : []
      });
    });

    if (isAdmin) {
      const invitedGuestGroups = Array.isArray(conviventie.invitedGuestGroups)
        ? conviventie.invitedGuestGroups
        : [];

      invitedGuestGroups.forEach((guestGroup) => {
        options.push({
          key: `guestGroup:${guestGroup.guestGroupId}`,
          targetType: 'guestGroup',
          targetId: guestGroup.guestGroupId,
          label: `Gastengroep: ${guestGroup.guestGroupName || '-'}`,
          downloadName: `Gastengroep: ${guestGroup.guestGroupName || '-'}`,
          uploadStatus: guestGroup.uploadStatus || 'pending',
          uploadedAt: guestGroup.uploadedAt || null,
          uploadedBy: guestGroup.uploadedBy || '',
          bevestigdeLeden: Array.isArray(guestGroup.bevestigdeLeden) ? guestGroup.bevestigdeLeden : []
        });
      });
    }

    return options;
  }, [gemeenschapById, isAdmin, userEmail]);

  // Conviventies the user can actually manage a ledenlijst for.
  const conviventieOptions = useMemo(
    () => conviventies.filter((conviventie) => targetOptionsFor(conviventie).length > 0),
    [conviventies, targetOptionsFor]
  );

  const selectedConviventie = useMemo(
    () => conviventieOptions.find((conviventie) => conviventie.id === selectedConviventieId) || null,
    [conviventieOptions, selectedConviventieId]
  );

  const targetOptions = useMemo(
    () => targetOptionsFor(selectedConviventie),
    [selectedConviventie, targetOptionsFor]
  );

  const selectedTarget = useMemo(
    () => targetOptions.find((option) => option.key === selectedTargetKey) || null,
    [targetOptions, selectedTargetKey]
  );

  // Keep both selectors valid while the underlying data changes.
  useEffect(() => {
    if (loading) return;

    if (conviventieOptions.length === 0) {
      if (selectedConviventieId) setSelectedConviventieId('');
      if (selectedTargetKey) setSelectedTargetKey('');
      return;
    }

    if (!selectedConviventie) {
      setSelectedConviventieId(conviventieOptions[0].id);
      return;
    }

    if (targetOptions.length > 0 && !selectedTarget) {
      setSelectedTargetKey(targetOptions[0].key);
    }
  }, [
    conviventieOptions,
    loading,
    selectedConviventie,
    selectedConviventieId,
    selectedTarget,
    selectedTargetKey,
    targetOptions
  ]);

  // Apply a target handed over from the Conviventies page (URL params).
  useEffect(() => {
    if (!requestedConviventieId) return;

    setSelectedConviventieId(requestedConviventieId);
    setSelectedTargetKey(requestedTargetKey);
  }, [requestedConviventieId, requestedTargetKey]);

  useEffect(() => {
    let isCancelled = false;

    const loadLeden = async () => {
      if (!selectedConviventieId || !selectedTargetKey.includes(':')) {
        setLeden([]);
        setUploadMeta({ uploadedAt: null, uploadedBy: '' });
        return;
      }

      const [targetType, targetId] = selectedTargetKey.split(':');

      try {
        setLedenLoading(true);
        const uploaded = await getUploadedLeden(selectedConviventieId, targetId, targetType);

        if (isCancelled) return;

        setLeden(uploaded.leden || []);
        setUploadMeta({
          uploadedAt: uploaded.uploadedAt || null,
          uploadedBy: uploaded.uploadedBy || ''
        });
      } catch (error) {
        if (isCancelled) return;
        console.error('Error loading leden:', error);
        alert('Fout bij het laden van de leden');
      } finally {
        if (!isCancelled) setLedenLoading(false);
      }
    };

    loadLeden();

    return () => {
      isCancelled = true;
    };
  }, [selectedConviventieId, selectedTargetKey, ledenRefreshToken]);

  const refreshAfterMutation = useCallback(async () => {
    await loadData();
    setLedenRefreshToken((token) => token + 1);
  }, [loadData]);

  const openAddLid = () => {
    setEditingLid(null);
    setFormData(emptyLidForm);
    setFormErrors({});
    setIsModalOpen(true);
  };

  const openEditLid = (lid) => {
    setEditingLid(lid);
    setFormData({
      voornaam: lid.voornaam || '',
      achternaam: lid.achternaam || '',
      aantal: lid.aantal || deriveLidAantalFromStatus(lid.status),
      status: lid.status || '',
      bijzonderheden: lid.bijzonderheden || '',
      geboortedatum: lid.geboortedatum || '',
      email: lid.email || '',
      telefoon: lid.telefoon || ''
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingLid(null);
    setFormData(emptyLidForm);
    setFormErrors({});
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'status' ? { aantal: deriveLidAantalFromStatus(value) } : {})
    }));

    setFormErrors((prev) => (prev[name] ? { ...prev, [name]: '' } : prev));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.voornaam.trim()) {
      errors.voornaam = 'Voornaam is verplicht';
    }

    if (!formData.achternaam.trim()) {
      errors.achternaam = 'Achternaam is verplicht';
    }

    if (!formData.status || !formData.status.trim()) {
      errors.status = 'Status is verplicht';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Manual member edits are non-destructive (Feature 35): a confirmation stays in
  // place, confirmed members keep their aankomst/vertrek and notes, and only a full
  // Excel re-upload (LedenUploadPanel) resets the confirmation.
  const handleSubmitLid = async () => {
    if (!selectedConviventieId || !selectedTarget || !validateForm()) return;

    const payload = {
      voornaam: formData.voornaam.trim(),
      achternaam: formData.achternaam.trim(),
      status: formData.status,
      geboortedatum: formData.geboortedatum || '',
      email: formData.email.trim(),
      telefoon: formData.telefoon.trim(),
      bijzonderheden: formData.bijzonderheden.trim()
    };

    const isGuestGroup = selectedTarget.targetType === 'guestGroup';

    try {
      if (editingLid) {
        await updateUploadedLid(
          selectedConviventieId,
          selectedTarget.targetId,
          selectedTarget.targetType,
          editingLid.id,
          payload
        );
        trackMemberEdited({ status: payload.status, is_guest_group: isGuestGroup });
      } else {
        await addUploadedLid(
          selectedConviventieId,
          selectedTarget.targetId,
          selectedTarget.targetType,
          payload,
          { expiresAt: selectedConviventie?.expiresAt || null }
        );
        trackMemberAdded({ status: payload.status, is_guest_group: isGuestGroup });
      }

      closeModal();
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error saving lid:', error);
      alert('Fout bij het opslaan van het lid');
    }
  };

  const handleDeleteLid = async (lidId) => {
    if (!selectedConviventieId || !selectedTarget) return;

    try {
      const deletedLid = leden.find((lid) => lid.id === lidId);

      await deleteUploadedLid(
        selectedConviventieId,
        selectedTarget.targetId,
        selectedTarget.targetType,
        lidId
      );
      trackMemberDeleted({
        status: deletedLid?.status,
        is_guest_group: selectedTarget.targetType === 'guestGroup'
      });

      await refreshAfterMutation();
    } catch (error) {
      console.error('Error deleting lid:', error);
      alert('Fout bij het verwijderen van het lid');
    }
  };

  if (loading) {
    return (
      <div className="leden-page">
        <div className="loading">Laden...</div>
      </div>
    );
  }

  return (
    <div className="leden-page">
      <div className="app-page-header">
        <div className="app-page-heading">
          <div className="app-page-title-row">
            <h2 className="app-page-title">Leden</h2>
            <SectionHelpButton
              title="Leden"
              purpose="Hier beheert u de ledenlijst van een gemeenschap of gastengroep voor een specifieke conviventie."
              instructions={[
                'Kies eerst een conviventie en daarna de gemeenschap (of gastengroep) waarvoor u de leden beheert.',
                'Gebruik Download template (of Download ledenlijst zodra er leden zijn) om het Excel-bestand op te halen; de huidige leden worden dan alvast ingevuld.',
                'Gebruik Kies bestand en Upload ledenlijst om een Excel-ledenlijst te laden of te vervangen.',
                'Gebruik + Nieuw lid om leden handmatig toe te voegen, het potlood om te bewerken en het kruisje om te verwijderen.',
                'Handmatige wijzigingen laten een bestaande bevestiging intact: bevestigde leden houden hun aankomst- en vertrekdatums en bijzonderheden.',
                'Een nieuwe Excel-ledenlijst uploaden vervangt de hele lijst en wist de bevestiging.',
                'Bevestig de deelname daarna via de knop Bevestig deelname op de Conviventies-pagina.'
              ]}
            />
          </div>
          <p className="app-page-description">
            Beheer de ledenlijst per conviventie, gemeenschap en gastengroep.
          </p>
        </div>
      </div>

      {conviventieOptions.length === 0 ? (
        <EmptyState
          icon="👥"
          message="Geen ledenlijsten beschikbaar"
          hint="Leden horen bij een conviventie. Zodra u voor een conviventie bent uitgenodigd, verschijnt de gemeenschap hier."
        />
      ) : (
        <>
          <div className="leden-selector">
            <div className="selector-field">
              <label htmlFor="conviventie-select">Conviventie:</label>
              <select
                id="conviventie-select"
                className="selector-input"
                value={selectedConviventieId}
                onChange={(e) => {
                  setSelectedConviventieId(e.target.value);
                  setSelectedTargetKey('');
                }}
              >
                {conviventieOptions.map((conviventie) => (
                  <option key={conviventie.id} value={conviventie.id}>
                    {conviventie.naam} ({conviventie.datum})
                  </option>
                ))}
              </select>
            </div>

            <div className="selector-field">
              <label htmlFor="target-select">Gemeenschap / gastengroep:</label>
              <select
                id="target-select"
                className="selector-input"
                value={selectedTargetKey}
                onChange={(e) => setSelectedTargetKey(e.target.value)}
                disabled={targetOptions.length === 0}
              >
                {targetOptions.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {!selectedTarget ? (
            <EmptyState icon="👥" message="Geen ledenlijst beschikbaar voor deze conviventie" />
          ) : (
            <>
              {ledenLoading ? (
                <div className="loading">Leden laden...</div>
              ) : (
                <LedenList
                  leden={leden}
                  onAdd={openAddLid}
                  onEdit={openEditLid}
                  onDelete={handleDeleteLid}
                  canEdit
                />
              )}

              {selectedTarget.uploadStatus === 'pending' && (
                <p className="leden-note">
                  Nog geen ledenlijst voor deze{' '}
                  {selectedTarget.targetType === 'guestGroup' ? 'gastengroep' : 'gemeenschap'}. Upload
                  hieronder een Excel-ledenlijst of voeg leden handmatig toe met &quot;+ Nieuw lid&quot;.
                </p>
              )}

              {selectedTarget.uploadStatus === 'confirmed' && (
                <p className="leden-note">{CONFIRMED_HINT_MESSAGE}</p>
              )}

              <LedenUploadPanel
                conviventieId={selectedConviventieId}
                targetId={selectedTarget.targetId}
                targetType={selectedTarget.targetType}
                targetLabel={selectedTarget.label}
                downloadName={selectedTarget.downloadName}
                uploadStatus={selectedTarget.uploadStatus}
                leden={ledenLoading ? [] : leden}
                uploadedAt={uploadMeta.uploadedAt}
                uploadedBy={uploadMeta.uploadedBy}
                expiresAt={selectedConviventie?.expiresAt || null}
                onUploaded={refreshAfterMutation}
                onDeleted={refreshAfterMutation}
              />

              <EditLidModal
                isOpen={isModalOpen}
                onClose={closeModal}
                onSubmit={handleSubmitLid}
                isEditMode={!!editingLid}
                formData={formData}
                onChange={handleFormChange}
                formErrors={formErrors}
                onDelete={() => {
                  if (!editingLid) return;
                  handleDeleteLid(editingLid.id);
                  closeModal();
                }}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default LedenPage;

